import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Switch } from "@mantine/core";
import {
  RoomSectionViewsPanel,
  type RoomSectionCameraDirs,
  type RoomSectionRenderableItem,
} from "@multiconsult-group/multiplacer-ui";
import {
  getRoomAxes,
  getRoomSectionViewDirs,
  type IMcBimItem,
} from "@multiconsult-group/multiplacer-core";
import { DesignLeftSideBarTabs } from "@multiconsult-group/multiplacer-core";
import type { IIsoViewerRenderItem } from "./roomSectionViewSelectors";
import type {
  IViewerSectionCenterCoordinate,
  IViewerSelectedSection,
} from "../ViewerRedux/viewerSlice";
import { selectObjectAndRoomThunk } from "../DesignTab/designThunk";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  setActiveLeftSideBarTabInRedux,
  setRequestZoomExtentInRedux,
  selectShowOnlyRoomSectionItemsVisibleIn2d,
  setShowOnlyRoomSectionItemsVisibleIn2dInRedux,
  selectShowRoomSectionPerimeterItems,
  setShowRoomSectionPerimeterItemsInRedux,
} from "../DesignTab/designSlice";

export function buildRoomSectionCameraDirs(mcbimSpace: IMcBimItem): RoomSectionCameraDirs {
  const viewDirs = getRoomSectionViewDirs(mcbimSpace);

  return {
    sideA: { x: viewDirs.sideA[0], y: viewDirs.sideA[1], z: 0 },
    sideB: { x: viewDirs.sideB[0], y: viewDirs.sideB[1], z: 0 },
    sideC: { x: viewDirs.sideC[0], y: viewDirs.sideC[1], z: 0 },
    sideD: { x: viewDirs.sideD[0], y: viewDirs.sideD[1], z: 0 },
    isometric: { x: 1, y: 1, z: 1 },
  };
}

type RoomSectionViewsPanelContentProps = {
  mcbimSpace: IMcBimItem | null | undefined;
  renderItems: IIsoViewerRenderItem[];
  sectionCenterCoordinate: IViewerSectionCenterCoordinate | null;
  selectedSection: IViewerSelectedSection;
  onSelectedSectionChange: (section: IViewerSelectedSection) => void;
  background?: string;
  showVisible2dToggle?: boolean;
};

export const RoomSectionViewsPanelContent: React.FC<RoomSectionViewsPanelContentProps> = ({
  mcbimSpace,
  renderItems,
  sectionCenterCoordinate,
  selectedSection,
  onSelectedSectionChange,
  background = "#f3f4f6",
  showVisible2dToggle = false,
}) => {
  const dispatch = useAppDispatch();
  const activeLeftSideBarTab = useAppSelector((state) => state.design.activeLeftSideBarTab);
  const showOnlyVisibleIn2d = useAppSelector(selectShowOnlyRoomSectionItemsVisibleIn2d);
  const showPerimeterItems = useAppSelector(selectShowRoomSectionPerimeterItems);
  const activeLeftSideBarTabRef = useRef(activeLeftSideBarTab);

  useEffect(() => {
    activeLeftSideBarTabRef.current = activeLeftSideBarTab;
  }, [activeLeftSideBarTab]);

  const cameraDirs = useMemo(
    () => (mcbimSpace ? buildRoomSectionCameraDirs(mcbimSpace) : null),
    [mcbimSpace]
  );
  const hasRoomAxes = useMemo(() => {
    if (!mcbimSpace) return false;

    try {
      getRoomAxes(mcbimSpace);
      return true;
    } catch {
      return false;
    }
  }, [mcbimSpace]);
  const handleItemClick = useCallback(
    (item: RoomSectionRenderableItem) => {
      if (item._id == null) {
        return;
      }

      dispatch(selectObjectAndRoomThunk(String(item._id)));

      if (activeLeftSideBarTabRef.current !== DesignLeftSideBarTabs.MCBIMITEM_INFO) {
        dispatch(setActiveLeftSideBarTabInRedux(DesignLeftSideBarTabs.MCBIMITEM_INFO));
        window.requestAnimationFrame(() => {
          dispatch(setRequestZoomExtentInRedux());
        });
      }
    },
    [dispatch]
  );
  const headerControls = showVisible2dToggle ? (
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <Switch
        size="sm"
        label="Vis vegger/dører/vinduer"
        checked={showPerimeterItems}
        onChange={(event) =>
          dispatch(setShowRoomSectionPerimeterItemsInRedux(event.currentTarget.checked))
        }
      />
      <Switch
        size="sm"
        label="Vis samme som 2D"
        checked={showOnlyVisibleIn2d}
        onChange={(event) =>
          dispatch(setShowOnlyRoomSectionItemsVisibleIn2dInRedux(event.currentTarget.checked))
        }
      />
    </div>
  ) : null;

  if (!cameraDirs || !hasRoomAxes) {
    return (
      <RoomSectionViewsPanel
        cameraDirs={{
          sideA: { x: 1, y: 0, z: 0 },
          sideB: { x: 0, y: 1, z: 0 },
          sideC: { x: -1, y: 0, z: 0 },
          sideD: { x: 0, y: -1, z: 0 },
          isometric: { x: 1, y: 1, z: 1 },
        }}
        renderItems={[]}
        sectionCenterCoordinate={null}
        selectedSection={selectedSection}
        onSelectedSectionChange={onSelectedSectionChange}
        onItemClick={handleItemClick}
        background={background}
        headerControls={headerControls}
      />
    );
  }

  return (
    <RoomSectionViewsPanel
      cameraDirs={cameraDirs}
      renderItems={renderItems}
      sectionCenterCoordinate={sectionCenterCoordinate}
      selectedSection={selectedSection}
      onSelectedSectionChange={onSelectedSectionChange}
      onItemClick={handleItemClick}
      background={background}
      headerControls={headerControls}
    />
  );
};
